import { useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Visibility, VisibilityOff, ContentCopy } from "@mui/icons-material";
import { useSnackbar } from "src/utils/useSnackbar";
import { useTranslation } from "react-i18next";

interface SecretKeyFieldProps {
    secretKey: string;
    label?: string;
}

export const SecretKeyField = ({ secretKey, label }: SecretKeyFieldProps) => {
    const [showSecret, setShowSecret] = useState(false);
    const { setSnackbarProps } = useSnackbar();
    const { t } = useTranslation();

    const toggleVisibility = () => {
        setShowSecret(!showSecret);
    };

    const copyToClipboard = () => {
        navigator.clipboard
            .writeText(secretKey)
            .then(() => {
                setSnackbarProps({
                    message: t("applications.secretCopied", "Secret key copied to clipboard"),
                    type: "success",
                });
            })
            .catch(() => {
                setSnackbarProps({
                    message: t("applications.secretCopyFailed", "Failed to copy secret key"),
                    type: "error",
                });
            });
    };

    const maskedValue = secretKey ? "•".repeat(Math.min(secretKey.length, 16)) : "N/A";

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {label && (
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    {label}:
                </Typography>
            )}
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {showSecret ? secretKey : maskedValue}
            </Typography>
            <Tooltip
                title={
                    showSecret ?
                        t("applications.hideSecret", "Hide secret")
                    :   t("applications.showSecret", "Show secret")
                }
            >
                <IconButton
                    size="small"
                    onClick={toggleVisibility}
                    aria-label={showSecret ? "hide secret" : "show secret"}
                >
                    {showSecret ?
                        <VisibilityOff fontSize="small" />
                    :   <Visibility fontSize="small" />}
                </IconButton>
            </Tooltip>
            <Tooltip title={t("applications.copySecret", "Copy to clipboard")}>
                <IconButton size="small" onClick={copyToClipboard} aria-label="copy to clipboard">
                    <ContentCopy fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
};
